import React from "react";
import Swal from "sweetalert2";

export const WP_MAX_WORDS = 8;
export const WP_WORD_MAX_LEN = 13;
export const WP_HINT_MAX_LEN = 100;
export const WP_MIN_WORDS = 2;

function sanitizeWordPuzzleWord(value) {
  return String(value ?? "").replace(/\d/g, "");
}

export const HINT_DISPLAY_MODES = [
  { value: "show_full_word", label: "Show Full Word" },
  { value: "show_first_letter", label: "Show First Letter" },
  { value: "show_hint_only", label: "Show Hint Only" },
];

export function emptyWordEntry(id) {
  return { id, word: "", hint: "" };
}

export function normalizeWordEntriesFromTheme(raw) {
  let list = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else if (raw && typeof raw === "object") {
    list = raw.words || raw.entries || [];
  }
  if (!list.length) {
    return [
      emptyWordEntry(`wp-${Date.now()}-0`),
      emptyWordEntry(`wp-${Date.now()}-1`),
    ];
  }
  return list.map((item, i) => ({
    id: `wp-${i}-${Date.now()}`,
    word: sanitizeWordPuzzleWord(item?.word ?? item?.name ?? "").slice(0, WP_WORD_MAX_LEN),
    hint: String(item?.hint ?? item?.indication ?? "").slice(0, WP_HINT_MAX_LEN),
  }));
}

export function parseHintDisplayMode(data) {
  const wp = data?.wordpuzzle;
  const raw =
    (typeof wp === "object" && wp !== null && !Array.isArray(wp) ? wp.hint_display_mode : null) ||
    data?.wordpuzzle_hint_display_mode ||
    data?.hint_display_mode ||
    "show_full_word";
  const found = HINT_DISPLAY_MODES.find((m) => m.value === raw || m.label === raw);
  return found?.value || "show_full_word";
}

export function clampWordEntry(entry) {
  return {
    word: sanitizeWordPuzzleWord(entry.word ?? "").trim().slice(0, WP_WORD_MAX_LEN),
    hint: String(entry.hint ?? "").trim().slice(0, WP_HINT_MAX_LEN),
  };
}

export function validateWordPuzzleEntries(entries) {
  const filled = entries.map((e) => clampWordEntry(e)).filter((e) => e.word);

  if (entries.some((e) => !String(e.word ?? "").trim() && String(e.hint ?? "").trim())) {
    return { ok: false, message: "Each row with a hint must also have a word." };
  }

  if (entries.some((e) => !String(e.word ?? "").trim()) && filled.length > 0) {
    return { ok: false, message: "Remove empty word rows or fill in every word field." };
  }

  if (filled.length < WP_MIN_WORDS) {
    return { ok: false, message: `Add at least ${WP_MIN_WORDS} word before saving.` };
  }

  if (filled.length > WP_MAX_WORDS) {
    return { ok: false, message: `You can add up to ${WP_MAX_WORDS} words.` };
  }

  const hasNumbers = filled.find((e) => /\d/.test(e.word));
  if (hasNumbers) {
    return { ok: false, message: "Words cannot contain numbers." };
  }

  return {
    ok: true,
    data: filled.map((e) => ({ word: e.word, hint: e.hint })),
  };
}

export default function WordPuzzleTab({
  entries,
  setEntries,
  hintDisplayMode,
  setHintDisplayMode,
  isSaving,
  onSave,
  errorMessage,
  successMessage,
  setErrorMessage,
  setSuccessMessage,
  newEntryId,
}) {
  const addMore = () => {
    if (entries.length >= WP_MAX_WORDS) {
      setErrorMessage(`Maximum ${WP_MAX_WORDS} words allowed.`);
      return;
    }
    setEntries((prev) => [...prev, emptyWordEntry(newEntryId())]);
    setErrorMessage("");
  };

  const updateEntry = (id, field, value) => {
    const sanitized =
      field === "word" ? sanitizeWordPuzzleWord(value) : String(value ?? "");
    const max = field === "word" ? WP_WORD_MAX_LEN : WP_HINT_MAX_LEN;
    setEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, [field]: sanitized.slice(0, max) } : e))
    );
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete this word?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
    });
    if (!result.isConfirmed) return;

    setEntries((prev) => {
      if (prev.length <= WP_MIN_WORDS) {
        Swal.fire("Limit", `You need at least ${WP_MIN_WORDS} words.`, "warning");
        return prev;
      }
      return prev.filter((e) => e.id !== id);
    });
    setSuccessMessage("Word removed.");
    setErrorMessage("");
  };

  return (
    <div className="themeupdate-panel-wide wordpuzzle-panel">
      <div className="wordpuzzle-card themeupdate-card">
        <div className="themeupdate-card-header">Words</div>
        <div className="themeupdate-card-body">
          <h3 className="wordpuzzle-subtitle">Word Indication Message</h3>
          <p className="wordpuzzle-limits">
            * {WP_MIN_WORDS}–{WP_MAX_WORDS} words · Word length — {WP_WORD_MAX_LEN} characters ·
            Numbers not allowed in words · Hint limit — {WP_HINT_MAX_LEN} characters
          </p>

          {(errorMessage || successMessage) && (
            <div className="qa-alerts">
              {errorMessage ? <p className="cr-alert cr-alert-error">{errorMessage}</p> : null}
              {successMessage ? <p className="cr-alert cr-alert-success">{successMessage}</p> : null}
            </div>
          )}

          <div className="wordpuzzle-rows">
            {entries.map((entry) => (
              <div key={entry.id} className="wordpuzzle-row">
                <div className="wordpuzzle-field wordpuzzle-field--word">
                  <label htmlFor={`wp-word-${entry.id}`}>Word:</label>
                  <input
                    id={`wp-word-${entry.id}`}
                    type="text"
                    value={entry.word}
                    maxLength={WP_WORD_MAX_LEN}
                    onChange={(e) => updateEntry(entry.id, "word", e.target.value)}
                    placeholder="Word"
                  />
                </div>
                <div className="wordpuzzle-field wordpuzzle-field--hint">
                  <label htmlFor={`wp-hint-${entry.id}`}>Hint (optional):</label>
                  <input
                    id={`wp-hint-${entry.id}`}
                    type="text"
                    value={entry.hint}
                    maxLength={WP_HINT_MAX_LEN}
                    onChange={(e) => updateEntry(entry.id, "hint", e.target.value)}
                    placeholder="Indication"
                  />
                </div>
                <button
                  type="button"
                  className="wordpuzzle-delete"
                  onClick={() => handleDelete(entry.id)}
                  aria-label="Delete word"
                  title="Delete"
                >
                  <i className="fa-regular fa-trash-can" />
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="wordpuzzle-add-more"
            onClick={addMore}
            disabled={entries.length >= WP_MAX_WORDS}
          >
            Add More
          </button>

          <div className="wordpuzzle-hint-mode">
            <label htmlFor="wp-hint-mode">Hint Display Mode:</label>
            <select
              id="wp-hint-mode"
              className="themeupdate-input wordpuzzle-select"
              value={hintDisplayMode}
              onChange={(e) => setHintDisplayMode(e.target.value)}
            >
              {HINT_DISPLAY_MODES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          <div className="wordpuzzle-save-wrap">
            <button
              type="button"
              className="wordpuzzle-save-btn"
              onClick={onSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
